import { useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Alert, Box, Button, CircularProgress, Collapse, Divider, Stack, Typography } from '@mui/material';

import type { SubjectLanguage } from '../types';
import type { DisplayState } from '../vocabulary';

import { type DeliveryHistoryEntry,DeliveryHistoryList } from './DeliveryHistoryList';
import { RequirementStateChip } from './RequirementStateChip';
import { WaiveRequirementDialog } from './WaiveRequirementDialog';

/**
 * One row, with its display state already derived by the container. The
 * per-patient DTO now carries its own `label` and `kind`, so naming a row no
 * longer needs a join against the org catalog.
 */
export interface RequirementRowView {
  patientRequirementId: string;
  label: string;
  state: DisplayState;
  /** "Requested by SMS 4 days ago", "Waived — patient pays cash". */
  detail?: string;
  /** AC-4: absent for settled rows, which have nothing to chase. */
  sendActionLabel?: string;
  /**
   * Why there is no send action on an otherwise-actionable row: the patient has
   * no reachable channel. Set only when the row would otherwise offer one.
   */
  sendBlockedReason?: string;
  /** AC-8: only an outstanding row can be waived. */
  canWaive?: boolean;
  /** AC-8: only a waived row can be reopened. */
  canReopen?: boolean;
}

export interface PatientRequirementsSectionViewProps {
  rows: RequirementRowView[];
  /** Rows still owed — the count in the header (AC-3). */
  missingCount: number;
  isLoading: boolean;
  isError: boolean;
  /** Delivery history for the whole patient (AC-20), shared by the rows. */
  deliveryHistory: DeliveryHistoryEntry[];
  /** The history failed to load — never rendered as "nothing has been sent". */
  isHistoryError?: boolean;
  onRetryHistory?: () => void;
  /**
   * The contact lookup failed, so no channel could be derived. Section-level
   * because it affects every row equally, and deliberately NOT phrased as the
   * per-row "no contact details" message — that would send staff to fix a
   * record that may already be correct.
   */
  isContactError?: boolean;
  onRetryContact?: () => void;
  /**
   * Requirement ids with a write in flight. Only those rows' controls disable —
   * a set rather than one id so overlapping actions do not release each other.
   */
  busyRequirementIds?: ReadonlySet<string>;
  subject: SubjectLanguage;
  onSend: (patientRequirementId: string) => void;
  onWaive: (patientRequirementId: string, reason: string) => void;
  onReopen: (patientRequirementId: string) => void;
  onRetry: () => void;
}

/**
 * The Requirements section on the patient record (GDN-3226; AC-3, AC-4, AC-8,
 * AC-20).
 *
 * Waive and reopen are offered per row rather than per section, and only where
 * they can actually act: `canWaive` on outstanding rows, `canReopen` on waived
 * ones. Withholding the control — not just the handler — is deliberate, so
 * there is never a button that looks live and does nothing.
 */
export const PatientRequirementsSectionView = ({
  rows,
  missingCount,
  isLoading,
  isError,
  deliveryHistory,
  isHistoryError,
  onRetryHistory,
  isContactError,
  onRetryContact,
  busyRequirementIds,
  subject,
  onSend,
  onWaive,
  onReopen,
  onRetry,
}: PatientRequirementsSectionViewProps) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [waiving, setWaiving] = useState<RequirementRowView | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        We could not load requirements for this {subject.singular}.
      </Alert>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
        This practice has no requirements set up.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: ELIG_TOKENS.ink }}>
          {missingCount === 0
            ? 'Nothing outstanding'
            : `${missingCount} item${missingCount === 1 ? '' : 's'} still needed`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* Also shown when the history FAILED: gating the toggle on a
            non-empty list made a failed load indistinguishable from no
            deliveries, because both left the list empty. */}
        {(deliveryHistory.length > 0 || isHistoryError) && (
          <Button
            size="small"
            variant="text"
            onClick={() => setHistoryOpen(open => !open)}
            sx={{ textTransform: 'none', fontSize: '0.75rem', color: ELIG_TOKENS.inkSoft }}
          >
            {historyOpen ? 'Hide delivery history' : 'Delivery history'}
          </Button>
        )}
      </Stack>

      <Collapse in={historyOpen} unmountOnExit>
        <Box sx={{ my: 1 }}>
          {/* A failed REFRESH must not discard records we already hold. The
              full alert is only for the case where there is nothing to show;
              with entries in hand the notice sits above them and they stay
              readable, secure links included. */}
          {isHistoryError && deliveryHistory.length === 0 ? (
            <Alert
              severity="warning"
              action={
                onRetryHistory && (
                  <Button color="inherit" size="small" onClick={onRetryHistory}>
                    Retry
                  </Button>
                )
              }
            >
              We could not load what has been sent. This does not mean nothing
              was sent.
            </Alert>
          ) : (
            <>
              {isHistoryError && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1 }}
                  action={
                    onRetryHistory && (
                      <Button color="inherit" size="small" onClick={onRetryHistory}>
                        Retry
                      </Button>
                    )
                  }
                >
                  Could not refresh. Showing what we last loaded, which may be
                  out of date.
                </Alert>
              )}
              <DeliveryHistoryList entries={deliveryHistory} />
            </>
          )}
        </Box>
      </Collapse>

      {isContactError && (
        <Alert
          severity="warning"
          sx={{ my: 1 }}
          action={
            onRetryContact && (
              <Button color="inherit" size="small" onClick={onRetryContact}>
                Retry
              </Button>
            )
          }
        >
          We could not check how to reach this {subject.singular}, so sending is
          unavailable. This is not a problem with their contact details.
        </Alert>
      )}

      <Divider />

      {rows.map(row => {
        const settled = row.state === 'received' || row.state === 'waived' || row.state === 'cancelled';
        const busy = busyRequirementIds?.has(row.patientRequirementId) ?? false;
        return (
          <Box key={row.patientRequirementId} sx={{ py: 1.25, opacity: settled ? 0.75 : 1 }}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
            >
              <Typography
                sx={{ fontSize: 14.5, fontWeight: 650, color: ELIG_TOKENS.ink, minWidth: 180 }}
              >
                {row.label}
              </Typography>
              <RequirementStateChip state={row.state} />
              <Box sx={{ flex: 1 }} />
              {row.sendActionLabel && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => onSend(row.patientRequirementId)}
                  startIcon={
                    busy ? <CircularProgress size={12} color="inherit" /> : undefined
                  }
                  sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                >
                  {row.sendActionLabel}
                </Button>
              )}
              {row.canWaive && (
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => setWaiving(row)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', color: ELIG_TOKENS.inkSoft }}
                >
                  Waive
                </Button>
              )}
              {row.canReopen && (
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => onReopen(row.patientRequirementId)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', color: ELIG_TOKENS.inkSoft }}
                >
                  Reopen
                </Button>
              )}
            </Stack>
            {row.detail && (
              <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft, lineHeight: 1.4 }}>
                {row.detail}
              </Typography>
            )}
            {/* Says what to fix rather than leaving a row with no action and no
                explanation. Server-side derivation lands in GDN-3356. */}
            {row.sendBlockedReason && (
              <Typography
                sx={{ fontSize: 12.5, color: 'warning.dark', lineHeight: 1.4 }}
              >
                {row.sendBlockedReason}
              </Typography>
            )}
          </Box>
        );
      })}

      <WaiveRequirementDialog
        row={waiving}
        subject={subject}
        onClose={() => setWaiving(null)}
        onConfirm={reason => {
          if (waiving) onWaive(waiving.patientRequirementId, reason);
          setWaiving(null);
        }}
      />
    </Stack>
  );
};
